import React, { useCallback, useEffect, useState } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal } from '../ui';
import { ClipboardList, Check, Loader2, Users } from 'lucide-react';

function formatCloses(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'Poll closed';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days >= 1) return `Closes in ${days}d ${hours}h`;
  return `Closes in ${hours}h`;
}

export default function Poll() {
  const { role, user, getCurrentPoll, votePoll, createManualPoll } = useSpc();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const canSee = role === 'admin' || user?.role === 'Coach' || user?.role === 'Run Leader' || (user?.permissions || []).includes('view_polls');

  const refresh = useCallback(async () => {
    try {
      const { data } = await getCurrentPoll();
      const p = data.poll;
      setPoll(p);
      setSelected(new Set(p?.myVote || []));
    } catch (e) {}
    setLoading(false);
  }, [getCurrentPoll]);

  useEffect(() => { if (canSee) refresh(); else setLoading(false); }, [canSee, refresh]);

  const [savingIds, setSavingIds] = useState(new Set());

  const commit = async (nextSet) => {
    if (!poll) return;
    const arr = Array.from(nextSet);
    const data = await votePoll(poll.id, arr);
    if (data?.poll) setPoll(data.poll);
  };

  const toggle = async (oid) => {
    // Update UI instantly, then persist in background
    const next = new Set(selected);
    if (next.has(oid)) next.delete(oid); else next.add(oid);
    setSelected(next);
    setSavingIds(s => { const n = new Set(s); n.add(oid); return n; });
    await commit(next);
    setSavingIds(s => { const n = new Set(s); n.delete(oid); return n; });
  };

  const clearAll = async () => {
    setSelected(new Set());
    await commit(new Set());
  };

  if (!canSee) {
    return (
      <div data-testid="poll-view">
        <Reveal className="section-head">
          <div><div className="eyebrow">Leader poll</div><h2>Leaders only</h2><p>The weekly session poll is only visible to Coaches and Run Leaders.</p></div>
        </Reveal>
        <div className="empty"><ClipboardList size={26} /><p>Ask an admin to promote you if you should be helping lead sessions.</p></div>
      </div>
    );
  }

  return (
    <div data-testid="poll-view">
      <Reveal className="section-head">
        <div><div className="eyebrow">Leader poll</div><h2>This week's session poll</h2><p>Tick every day you can lead a session. A fresh poll drops every Saturday for the coming week.</p></div>
      </Reveal>

      {loading ? (
        <div className="empty"><Loader2 size={20} className="spin" /> Loading poll…</div>
      ) : !poll ? (
        <div className="empty" data-testid="poll-empty">
          <ClipboardList size={26} />
          <p>No live poll right now — the next one drops on Saturday.</p>
          {role === 'admin' && (
            <button className="btn" data-testid="poll-manual-create" onClick={async () => { await createManualPoll(); refresh(); }}>Create this week's poll now</button>
          )}
        </div>
      ) : (
        <Reveal className="card poll-card" data-testid={`poll-${poll.id}`}>
          <div className="poll-head">
            <span className="pill yellow">Week of {new Date(poll.weekStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
            <span className="pill">{formatCloses(poll.closesAt)}</span>
            <span className="pill"><Users size={12} /> {poll.totalVoters} voted</span>
          </div>
          <h3 className="poll-question">{poll.question}</h3>
          <div className="poll-options">
            {poll.options.map(opt => {
              const checked = selected.has(opt.id);
              return (
                <label key={opt.id} className={`poll-option ${checked ? 'checked' : ''}`} data-testid={`poll-option-${opt.id}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(opt.id)} />
                  <div className="poll-option-body">
                    <div className="poll-option-title">
                      <span className="poll-option-label">{opt.label}</span>
                      {savingIds.has(opt.id)
                        ? <span className="poll-option-count"><Loader2 size={12} className="spin" /> Saving…</span>
                        : <span className="poll-option-count">{opt.count === 0 ? 'No votes yet' : `${opt.count} ${opt.count === 1 ? 'vote' : 'votes'}`}</span>}
                    </div>
                    {opt.voters.length > 0 && (
                      <div className="poll-option-voters" data-testid={`poll-voters-${opt.id}`}>
                        {opt.voters.slice(0, 8).map((v, i) => (
                          <span key={i} className="poll-voter-chip" data-testid={`poll-voter-${opt.id}-${i}`} title={v.role || ''}>
                            <span className="poll-voter-avatar">
                              {v.avatar ? <img src={v.avatar} alt="" /> : (v.name || '?').split(' ').map(x => x[0]).slice(0,2).join('')}
                            </span>
                            <span className="poll-voter-name">{v.name}</span>
                          </span>
                        ))}
                        {opt.voters.length > 8 && <span className="poll-voter-chip poll-voter-more">+{opt.voters.length - 8} more</span>}
                      </div>
                    )}
                  </div>
                  {checked && <Check size={16} className="poll-check" />}
                </label>
              );
            })}
          </div>
          <div className="poll-footer">
            {selected.size > 0
              ? <span className="poll-saved" data-testid="poll-status">✅ Your vote is saved — tap any tick to change it, or <button type="button" className="poll-clear-link" onClick={clearAll} data-testid="poll-clear">clear all</button></span>
              : <span className="poll-saved poll-saved-empty" data-testid="poll-status">Tap the days you can lead. Your vote saves automatically.</span>}
          </div>
        </Reveal>
      )}
    </div>
  );
}
